/**
 * =============================================================================
 * PATIENT JOURNEY - SHARED STYLE TOKENS
 * =============================================================================
 *
 * Every colour here points at the CSS variables declared in
 * `src/assets/css/style.css`, so the journey follows the app theme (including
 * dark mode) instead of carrying its own hex values.
 * =============================================================================
 */

import { OPTION_TONE } from "./journeyConfig";

export const FONT = "Figtree, sans-serif";

export const JOURNEY_COLORS = {
    accent: "var(--app-accent)",
    accentSoft: "var(--app-accent-soft)",

    success: "var(--app-success)",
    successSoft: "var(--app-success-bg)",

    warning: "var(--app-warning)",
    warningSoft: "var(--app-warning-bg)",

    danger: "var(--app-danger)",
    dangerSoft: "var(--app-danger-bg)",

    surface: "var(--app-surface)",
    surfaceMuted: "var(--app-surface-2)",

    /* Grooves that sit directly on the page background. --app-surface-2 is
       identical to --app-bg in the light theme, so anything drawn on the page
       itself (the tab track, a segmented control) needs this instead. */
    track: "var(--app-border)",

    border: "var(--app-border)",
    line: "var(--app-line)",

    text: "var(--app-text)",
    textMuted: "var(--app-text-muted)",
};

/* -----------------------------------------------------------------------------
   TONES
----------------------------------------------------------------------------- */

export const getToneColors = (tone) => {
    if (tone === OPTION_TONE.DANGER) {
        return {
            main: JOURNEY_COLORS.danger,
            soft: JOURNEY_COLORS.dangerSoft,
        };
    }

    if (tone === OPTION_TONE.WARNING) {
        return {
            main: JOURNEY_COLORS.warning,
            soft: JOURNEY_COLORS.warningSoft,
        };
    }

    return {
        main: JOURNEY_COLORS.accent,
        soft: JOURNEY_COLORS.accentSoft,
    };
};

/* -----------------------------------------------------------------------------
   TEXT
----------------------------------------------------------------------------- */

export const stepCaptionSx = {
    fontFamily: FONT,
    fontSize: "10px",
    lineHeight: "14px",
    fontWeight: 600,
    letterSpacing: "0.6px",
    color: JOURNEY_COLORS.textMuted,
};

export const stepTitleSx = {
    fontFamily: FONT,
    fontSize: "15px",
    lineHeight: "20px",
    fontWeight: 600,
    color: JOURNEY_COLORS.text,
};

export const stepDescriptionSx = {
    fontFamily: FONT,
    fontSize: "12px",
    lineHeight: "18px",
    fontWeight: 400,
    color: JOURNEY_COLORS.textMuted,
};

export const sectionTitleSx = {
    fontFamily: FONT,
    fontSize: "14px",
    lineHeight: "20px",
    fontWeight: 600,
    color: JOURNEY_COLORS.text,
    marginBottom: { xs: "12px", sm: "16px" },
};

export const helperTextSx = {
    fontFamily: FONT,
    fontSize: "11px",
    lineHeight: "16px",
    fontWeight: 500,
};

/* -----------------------------------------------------------------------------
   SURFACES
----------------------------------------------------------------------------- */

export const cardSx = (borderColor) => ({
    width: "100%",
    marginBottom: "8px",
    border: "1px solid " + (borderColor || JOURNEY_COLORS.border),
    borderRadius: "8px",
    backgroundColor: JOURNEY_COLORS.surface,
    boxSizing: "border-box",
    overflow: "hidden",
    transition: "border-color 0.2s ease",
});

/**
 * The amber "End Case" tag on an outcome row.
 */
export const endCaseChipSx = {
    display: "inline-flex",
    alignItems: "center",
    padding: "3px 8px",
    borderRadius: "5px",
    backgroundColor: JOURNEY_COLORS.warningSoft,
    fontFamily: FONT,
    fontSize: "10px",
    lineHeight: "13px",
    fontWeight: 600,
    color: JOURNEY_COLORS.warning,
    whiteSpace: "nowrap",
};

export const chipSx = (color, background) => ({
    display: "inline-flex",
    alignItems: "center",
    padding: "2px 7px",
    borderRadius: "4px",
    backgroundColor: background,
    fontFamily: FONT,
    fontSize: "9px",
    lineHeight: "12px",
    fontWeight: 700,
    letterSpacing: "0.3px",
    color: color,
    whiteSpace: "nowrap",
});

/* -----------------------------------------------------------------------------
   CONTROLS
----------------------------------------------------------------------------- */

export const primaryButtonSx = (enabled) => ({
    height: "44px",
    marginTop: "16px",
    borderRadius: "7px",
    textTransform: "none",
    fontFamily: FONT,
    fontSize: "13px",
    fontWeight: 600,
    boxShadow: "none",
    backgroundColor: enabled
        ? JOURNEY_COLORS.accent
        : JOURNEY_COLORS.track,
    color: enabled ? "#FFFFFF" : JOURNEY_COLORS.textMuted,
    "&:hover": {
        boxShadow: "none",
        backgroundColor: enabled
            ? JOURNEY_COLORS.accent
            : JOURNEY_COLORS.track,
        filter: enabled ? "brightness(0.94)" : "none",
    },
    "&.Mui-disabled": {
        backgroundColor: JOURNEY_COLORS.track,
        color: JOURNEY_COLORS.textMuted,
    },
});

export const inputSx = {
    "& .MuiOutlinedInput-root": {
        height: { xs: "44px", sm: "40px" },
        borderRadius: "7px",
        backgroundColor: JOURNEY_COLORS.surface,
        fontFamily: FONT,
        fontSize: "12px",
        color: JOURNEY_COLORS.text,
    },
    "& .MuiOutlinedInput-notchedOutline": {
        borderColor: JOURNEY_COLORS.border,
    },
    "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: JOURNEY_COLORS.line,
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: JOURNEY_COLORS.accent,
    },
    "& input::placeholder": {
        color: JOURNEY_COLORS.textMuted,
        opacity: 1,
    },
    "& .MuiFormHelperText-root": {
        fontFamily: FONT,
        fontSize: "10px",
        marginLeft: "2px",
    },
};

export const fieldLabelSx = {
    fontFamily: FONT,
    fontSize: "11px",
    lineHeight: "16px",
    fontWeight: 600,
    color: JOURNEY_COLORS.textMuted,
    marginBottom: "6px",
};
